import { useCallback, useMemo } from "react";
import { generatePath, useNavigate } from "react-router";

import { Reservation_List_Repeater_Type } from "@/lib/gql/__generated__/graphql";

import { PATHS } from "@/routes/paths";

import { Label } from "@/components/ui/label/label";
import { TableCell, TableRow } from "@/components/ui/table/table";

import { SearchResultTableToolTip } from "./tool-tip";

import type * as SharedModel from "../../model";

type Props = {
	row: SharedModel.Type.ViewData.SearchResultTableRowData;
};

export const SearchResultTableTBody = ({ row }: Props) => {
	const navigate = useNavigate();
	/**
	 * 予約詳細への遷移（別タブ）
	 */
	const handleClickTransitionToReservationDetail = useCallback(() => {
		navigate(
			generatePath(PATHS.HOTEL.MANAGEMENT.RESERVATION.DETAIL, {
				hotelID: row.hotelID,
				reservationID: row.reservationID,
			}),
		);
	}, [navigate, row.hotelID, row.reservationID]);

	/**
	 * リピータ表示用テキスト
	 */
	const repeaterLabelText = useMemo(() => {
		switch (row.repeaterType) {
			case Reservation_List_Repeater_Type.Repeater:
				return "リピータ";
			case Reservation_List_Repeater_Type.RepeaterOtherHotel:
				return "リピータ（他ホテルを含む）";
			case Reservation_List_Repeater_Type.NotRepeater:
			default:
				return "";
		}
	}, [row.repeaterType]);

	/**
	 * リピータホバー時の説明用テキスト
	 */
	const repeaterTooltipText = useMemo(() => {
		switch (row.repeaterType) {
			case Reservation_List_Repeater_Type.Repeater:
				return `リピータとは、同じメールアドレスで<br />再度ご予約いただいたお客様のことを指します。`;
			case Reservation_List_Repeater_Type.RepeaterOtherHotel:
				return `リピータ（他ホテルを含む）とは、同じメールアドレスで<br />再度ご予約いただいたお客様のことを指します。<br />ホテル一覧に表示されている、ホテルを跨いで判定しています。`;
			case Reservation_List_Repeater_Type.NotRepeater:
			default:
				return "";
		}
	}, [row.repeaterType]);

	/**
	 * リピータラベルを表示するか
	 */
	const shouldShowRepeaterLabel = useMemo(() => {
		return (
			row.repeaterType === Reservation_List_Repeater_Type.Repeater ||
			row.repeaterType === Reservation_List_Repeater_Type.RepeaterOtherHotel
		);
	}, [row.repeaterType]);

	return (
		<TableRow className="group hover:cursor-pointer" onClick={handleClickTransitionToReservationDetail}>
			{/* 予約番号 */}
			<TableCell className="sticky left-0 w-32 bg-white text-center group-hover:bg-zinc-50">
				<p>{row.reservationIDText}</p>
			</TableCell>

			{/* 予約タイプ */}
			<TableCell className=" bg-white text-center group-hover:bg-zinc-50 tablet:sticky tablet:left-32">
				<div className="grid justify-center gap-y-2">
					{shouldShowRepeaterLabel && ( //
						<SearchResultTableToolTip
							label={<Label variant="blue" text={repeaterLabelText} />}
							tooltipText={repeaterTooltipText}
						/>
					)}
					{row.reservationTypes.map(({ labelType, text }, index) => (
						<Label key={index} variant={labelType} text={text} />
					))}
				</div>
			</TableCell>

			{/* 状態 */}
			<TableCell className="text-left">
				<p className="whitespace-pre-wrap">{row.reservationStatusText}</p>
			</TableCell>

			{/* 氏名 */}
			<TableCell className="text-center">
				<p>{row.guestNameText}</p>
			</TableCell>

			{/* チェックイン */}
			<TableCell className="text-center">
				<p>{row.checkInText}</p>
			</TableCell>

			{/* チェックアウト */}
			<TableCell className="text-center">
				<p>{row.isDayTrip ? row.checkInText : row.checkOutText}</p>
			</TableCell>

			{/* 予約日 */}
			<TableCell className="text-center">
				<p>{row.reserveDateText}</p>
			</TableCell>

			{/* 合計料金（税込） */}
			<TableCell className="text-center">
				<p>{row.totalGuestChargeText}</p>
			</TableCell>

			{/* 決済方法 */}
			<TableCell className="text-center">
				<p>{row.paymentMethodText}</p>
			</TableCell>

			{/* 人数 */}
			<TableCell className="text-center">
				<p>{row.numOfPeopleText}</p>
			</TableCell>

			{/* 部屋 */}
			<TableCell className="text-center">
				<p className="whitespace-pre-wrap">{row.roomsText}</p>
			</TableCell>

			{/* プラン */}
			<TableCell className="text-center">
				<p>{row.planNameText}</p>
			</TableCell>

			{/* オプション */}
			<TableCell className="text-center">
				<p className="whitespace-pre-wrap">{row.optionText}</p>
			</TableCell>

			{/* ディスカウント */}
			<TableCell className="text-center">
				<p>{row.discountText}</p>
			</TableCell>

			{/* 電話番号 */}
			<TableCell className="text-center">
				<p>{row.guestTelText}</p>
			</TableCell>

			{/* メールアドレス */}
			<TableCell className="text-center">
				<p>{row.guestEmailText}</p>
			</TableCell>

			{/* 住所 */}
			<TableCell>
				<p>{row.guestPostalCodeText}</p>
				<p>{row.guestAddressText}</p>
			</TableCell>

			{/* メルマガ配信希望 */}
			<TableCell className="text-center">
				<p>{row.isAcceptMailMagazineText}</p>
			</TableCell>
		</TableRow>
	);
};
